import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'how-long-do-organisations-keep-your-records';

export const RecordRetentionPeriods = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        A common worry before requesting anything is that you have already left it too late — that
        somewhere along the way, your file was quietly destroyed. For almost everyone, that worry
        is unfounded. Records connected to your childhood, especially anything involving social
        care, are kept for genuinely long periods, in several cases for the rest of your life and
        beyond. This guide sets out, organisation by organisation, roughly how long — and is
        honest about which of these figures are fixed by law and which are simply the strong,
        long-standing convention almost everyone follows.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Social care" title="Council and children's home records: around 75 years">
      <p>
        For a children&apos;s home specifically, this is written directly into law: the{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2015/541/regulation/36">
          Children&apos;s Homes (England) Regulations 2015
        </SourceLink>{' '}
        require your case record to be kept for{' '}
        <strong className="text-ink">75 years from your date of birth</strong> (15 years from the
        date of death, if a child died before turning 18).
      </p>
      <p>
        For a council&apos;s own wider social care case file, the same 75-years-from-birth figure is
        overwhelmingly the standard councils use, following long-established local-government
        records-management guidance — but, unlike the children&apos;s home figure above, we could
        not trace it to one single piece of legislation that fixes it for every council. In
        practice it makes very little difference: it is such a consistently applied convention
        that a council telling you it destroyed a care record decades early would be a genuine
        outlier, worth asking hard questions about.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Fostering" title="Fostering agency records: at least 15 years from the last entry">
      <p>
        Fostering service providers — councils and independent fostering agencies alike — must
        keep their records for{' '}
        <strong className="text-ink">at least 15 years from the date of the last entry</strong>,
        under{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2011/581/regulation/22">
          regulation 22 of the Fostering Services (England) Regulations 2011
        </SourceLink>
        . Because that clock runs from the last entry, not from your birth, a placement that ran
        close to when you left care will have records held for a while past that point too. See
        our{' '}
        <Link to="/guides/how-to-request-your-fostering-records" className="text-brand underline hover:opacity-90">
          guide to requesting fostering records
        </Link>{' '}
        for more.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Adoption" title="Adoption records: 100 years, in most cases">
      <p>
        Adoption records get the longest, most precisely legislated retention of anything in this
        guide.{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2005/888/regulation/6/made">
          The Disclosure of Adoption Information (Post-Commencement Adoptions) Regulations 2005
        </SourceLink>{' '}
        require an adoption agency to keep the core adoption information for{' '}
        <strong className="text-ink">at least 100 years from the date of the adoption
        order</strong>, for adoptions made on or after 30 December 2005. For an adoption before
        that date, the figure currently used is 75 years from the order — though the government
        has been consulting on raising that to 100 years to match. See our{' '}
        <Link to="/guides/how-to-request-your-adoption-records" className="text-brand underline hover:opacity-90">
          guide to requesting adoption records
        </Link>{' '}
        for the fuller picture.
      </p>
    </GuideSection>

    <GuideCTA />

    <GuideSection eyebrow="School, health and Cafcass" title="Shorter, but still well past childhood">
      <GuideChecklist
        items={[
          <>
            <strong className="text-ink">School records</strong> — the official school records
            toolkit sets a pupil&apos;s whole record at{' '}
            <strong className="text-ink">25 years from their date of birth</strong>, with SEN and
            other support-service material kept longer still, to{' '}
            <strong className="text-ink">31 years from birth</strong>. See our{' '}
            <Link to="/guides/how-to-request-your-school-records" className="text-brand underline hover:opacity-90">
              guide to requesting school records
            </Link>
            .
          </>,
          <>
            <strong className="text-ink">GP and hospital records</strong> — the{' '}
            <SourceLink href="https://digital.nhs.uk/data-and-information/information-governance/guidance/records-management-code-of-practice/appendix-ii/retention-schedule-care-records">
              NHS Records Management Code of Practice
            </SourceLink>{' '}
            sets a child&apos;s health record to be kept until their{' '}
            <strong className="text-ink">25th birthday, or 26th if they were 17 when treatment
            ended</strong>. Maternity records get a straight 25 years, explicitly because — in the
            Code&apos;s own words — they are "as much the child&apos;s record as the parent&apos;s." See
            our{' '}
            <Link to="/guides/how-to-request-your-medical-records" className="text-brand underline hover:opacity-90">
              guide to requesting medical records
            </Link>
            .
          </>,
          <>
            <strong className="text-ink">Cafcass</strong> — case information is kept until the{' '}
            <strong className="text-ink">youngest child in your case turns 25</strong>, not from
            your own birth — so if siblings were involved, records can outlast your own 25th
            birthday by some way. See our{' '}
            <Link to="/guides/how-to-request-your-cafcass-and-family-court-records" className="text-brand underline hover:opacity-90">
              guide to Cafcass and family court records
            </Link>
            .
          </>,
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="Police" title="Police records: no single fixed period">
      <p>
        This is the one genuine exception to "long and predictable." Police retention is governed
        by a national framework — the{' '}
        <SourceLink href="https://www.college.police.uk/app/information-management/management-police-information/retention-review-and-disposal">
          Management of Police Information guidance
        </SourceLink>{' '}
        — that groups records by how serious the underlying matter was, and reviews them on
        scheduled or triggered bases rather than deleting everything on one fixed date. In
        practice, this means it genuinely varies, and it is not something we can give you a single
        number for. See our{' '}
        <Link to="/guides/how-to-request-your-police-records" className="text-brand underline hover:opacity-90">
          guide to requesting police records
        </Link>{' '}
        either way — the only way to know what still exists is to ask.
      </p>
    </GuideSection>

    <GuideNote>
      <p>
        <strong>These figures are for England.</strong> Scotland, Wales and Northern Ireland each
        have their own records-management rules, which can differ — Northern Ireland, for
        instance, keeps some child protection files until age 30 rather than 25. If your records
        sit outside England, treat the periods above as a rough guide, not an exact one, and ask
        the organisation directly.
      </p>
    </GuideNote>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="retention-faq">
        <GuideFaqItem
          q="An organisation told me my records were destroyed. What should I do?"
          a="Ask for the actual date they were destroyed and the specific policy that authorised it. Given how long these periods generally are, an early destruction date is worth questioning — and if the explanation doesn't add up, it's worth raising with the ICO."
        />
        <GuideFaqItem
          q="Does it matter how old I am now?"
          a="Not for whether you can ask — your right of access doesn't expire. It matters only for whether the record is still likely to exist, which these retention periods are here to help you judge before you assume the worst."
        />
        <GuideFaqItem
          q="Why isn't there one single rule for everything?"
          a="Each sector's retention period was set separately, for its own reasons — adoption's is set in its own regulations because of how permanently significant that information is, health records tie to when a young adult can act fully for themselves, and so on. There's no single UK-wide 'records law' underneath all of it."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        Each figure above links to its own source in the section it appears in. For requesting
        records generally, see our{' '}
        <Link to="/guides/what-is-a-subject-access-request" className="text-brand underline hover:opacity-90">
          plain-English guide to Subject Access Requests
        </Link>
        .
      </p>
    </GuideSection>

    <GuideCTA
      heading="Ask now rather than wonder"
      body="Whatever the retention period, the only way to know what still exists is to ask. Add the organisations you remember and we'll draft the requests, send them, and track every deadline for you."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default RecordRetentionPeriods;
